import React from "react";
import {
  Box, Badge, Button, Container, Grid, GridItem, Heading, HStack,
  Link, Text, UnorderedList, ListItem, VStack
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";

const brand = {
  green: "#1C4C3F",
  cream: "#F7F5EB",
  mint: "#D9EFE6",
  ink: "#0E2721",
};

function Card({ title, body, cta, to }) {
  return (
    <Box p={6} bg="#FFFFFF" border="1px solid #EAEAEA" borderRadius="lg">
      <VStack align="start" spacing={3}>
        <Badge bg="#D9EFE6" color="#0E2721" borderRadius="md" px={2} py={1}>{title}</Badge>
        <Text>{body}</Text>
        {to && (
          <Button as={RouterLink} to={to} variant="link" rightIcon={<ArrowForwardIcon />}>
            {cta || "Learn more"}
          </Button>
        )}
      </VStack>
    </Box>
  );
}

export default function Features() {
  return (
    <Box bg="#F7F5EB" color={brand.ink}>
      {/* Hero */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 14, md: 20 }}>
        <VStack align="start" spacing={4}>
          <Heading size="2xl" lineHeight={1.1}>
            Everything you need to turn CPG data into decisions
          </Heading>
          <Text fontSize="lg" opacity={0.9}>
            Role-aware agents, auto-charting, and pricing tools—built for CPG workflows, not generic chat.
          </Text>
          <HStack spacing={3} pt={2}>
            <Button as={RouterLink} to="/signup" bg={brand.green} color="white">
              Start free
            </Button>
          </HStack>
        </VStack>
      </Container>

      {/* Product pillars */}
      <Box bg="white">
        <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
            <Card
              title="AI CPG Advisors"
              body="On-demand experts for strategy, trade, category, buying, and analysis. Get slide-ready bullets: what happened, why it matters, what to do."
              to="/advisors"
              cta="See advisors"
            />
            <Card
              title="Auto Charting"
              body="Drop in a flat file and get clean, on-brand charts (bar, grouped, stacked, line, dual-axis). Export PNG/PDF in seconds."
              to="/charts"
              cta="Generate a chart"
            />
            <Card
              title="Pricing Tools"
              body="Quick margin math, promo ROI, and guardrails so you can price with confidence."
              to="/pricing-tools"
              cta="Open Pricing Tools"
            />
          </Grid>
        </Container>
      </Box>

      {/* Workflows */}
      <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 16 }}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={8}>
          <GridItem>
            <Heading size="lg" mb={4}>Common workflows</Heading>
            <UnorderedList spacing={3}>
              <ListItem>Build a 90-day commercial plan by channel</ListItem>
              <ListItem>Find velocity gaps and distribution opportunities</ListItem>
              <ListItem>Estimate promo lift & ROAS before you commit</ListItem>
              <ListItem>Turn weekly sales into buyer-ready slides</ListItem>
            </UnorderedList>
          </GridItem>
          <GridItem>
            <Heading size="lg" mb={4}>Privacy & control</Heading>
            <UnorderedList spacing={3}>
              <ListItem>Your data isn’t used to train public models</ListItem>
              <ListItem>Ephemeral by default; saving is opt-in</ListItem>
              <ListItem>One-click purge of datasets and chats</ListItem>
              <ListItem>TLS in transit; AES-256 at rest</ListItem>
            </UnorderedList>
          </GridItem>
        </Grid>
      </Container>

      {/* CTA */}
      <Box bg={brand.green} color="white">
        <Container maxW="6xl" px={{ base: 4, md: 6 }} py={12}>
          <HStack
            justify="space-between"
            flexDir={{ base: "column", md: "row" }}
            align="start"
            gap={4}
          >
            <VStack align="start" spacing={1}>
              <Heading size="md">Ready to try it with your data?</Heading>
              <Text opacity={0.9}>Start free.</Text>
            </VStack>
            <Button as={RouterLink} to="/signup" bg="white" color={brand.green}>
              Start free
            </Button>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
}
